import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { StorageService } from '../../../core/services/storage.service';
import { LoginRequest, LoginResponse } from '../models/auth.models';
import { CookieService } from '../../../core/services/cookie.service';
import { SignalRService } from '../../../shared/services/signal-r.service';
import { UserPreferencesService } from '../../../core/services/user-preferences.service';
import { AudioPreferenceService } from '../../../core/services/audio-preference.service';
import { SavedFiltersService } from '../../../shared/services/saved-filters.service';

export enum TokenBuffer {
    ACCESS_TOKEN = 60000,
    REFRESH_TOKEN = 10000,
}

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private http = inject(HttpClient);
    private storageService = inject(StorageService);
    private cookieService = inject(CookieService);
    private signalRService = inject(SignalRService);
    private userPreferencesService = inject(UserPreferencesService);
    private audioPreferenceService = inject(AudioPreferenceService);
    private savedFiltersService = inject(SavedFiltersService);

    isAuthenticated = signal<boolean>(false);
    isLoading = signal<boolean>(false);

    /**
     * Single source of truth for an in-flight token refresh (AB#8996).
     * While a refresh is running this holds the one shared, multicast
     * observable that every caller — the interceptor's parked requests and
     * the route guard alike — subscribes to, so they can never diverge into
     * two competing refreshes. It is reset to null once the refresh settles
     * (see performTokenRefresh) so the next refresh starts fresh.
     */
    private refreshInFlight$: Observable<LoginResponse> | null = null;

    private tokenExpiresAt: number | null = null;
    private refreshTokenExpiresAt: number | null = null;

    constructor() {
        this.initializeAuth();
    }

    private initializeAuth(): void {
        const hasValidSession = this.storageService.isLoggedIn();
        const hasRefreshToken = !!this.cookieService.getCookie('refreshToken');

        this.isAuthenticated.set(hasValidSession && hasRefreshToken);

        const expiry = this.storageService.getTokenExpiry();
        if (expiry) {
            this.tokenExpiresAt = expiry.tokenExpiresAt;
            this.refreshTokenExpiresAt = expiry.refreshTokenExpiresAt;
        }
    }

    login(credentials: LoginRequest): Observable<LoginResponse> {
        this.isLoading.set(true);

        return this.http
            .post<LoginResponse>(
                `${environment.authApiUrl}/Auth/Login`,
                credentials,
            )
            .pipe(
                tap((response) => {
                    this.saveAuthData(response);
                    // AB#9726 / UI-231: reload the per-user preference/audio
                    // services now that storage identifies the newly logged
                    // in user, so a same-tab A -> logout -> B switch has B
                    // seeing B's own page-size/landing/alert-sound values
                    // immediately rather than A's, stale in these
                    // root-singleton services until a hard refresh.
                    this.userPreferencesService.reloadForCurrentUser();
                    this.audioPreferenceService.reloadForCurrentUser();
                    this.savedFiltersService.reloadForCurrentUser();
                    this.isLoading.set(false);
                }),
                catchError((error) => {
                    this.isLoading.set(false);
                    return throwError(() => error);
                }),
            );
    }

    refreshToken(): Observable<LoginResponse> {
        const refreshToken = this.cookieService.getCookie('refreshToken');

        if (!refreshToken || this.isRefreshTokenExpired()) {
            this.logout();
            return throwError(
                () => new Error('Refresh token expired or not found'),
            );
        }

        // Reuse the single in-flight refresh so concurrent callers share one
        // POST and one outcome instead of racing independent refreshes.
        if (this.refreshInFlight$) {
            return this.refreshInFlight$;
        }

        this.refreshInFlight$ = this.performTokenRefresh(refreshToken);
        return this.refreshInFlight$;
    }

    logout(): void {
        this.storageService.clear();
        this.cookieService.deleteCookie('refreshToken');
        this.isAuthenticated.set(false);
        this.tokenExpiresAt = null;
        this.refreshTokenExpiresAt = null;
        this.refreshInFlight$ = null;
        // AB#8980: SignalRService is a root-scoped singleton, so its
        // connection + backlog/decided-id state otherwise survives a
        // logout/login cycle on the same tab (cross-user data-exposure risk
        // on shared trading-floor terminals). Tear it down here; the next
        // login remounts the authenticated Layout shell, whose ngOnInit calls
        // setupConnection()/initializeBetTickerBacklog() fresh.
        void this.signalRService.disconnect();
        // AB#9726 / UI-231: same class of bug as AB#8980 above, missed for
        // these two root-singleton services. Reset them to the anonymous
        // scope now that storage no longer identifies a user, so nothing
        // between this logout and the next login still reads the previous
        // user's page-size/landing/alert-sound values.
        this.userPreferencesService.reloadForCurrentUser();
        this.audioPreferenceService.reloadForCurrentUser();
        // AB#9774 / UI-271: SavedFiltersService invalidates its per-screen
        // cache by comparing user keys, which cannot detect a change when the
        // key is unresolvable for both users (a deployment with no
        // dataPulseUserId). Clear it explicitly so in-memory views cannot
        // outlive the session that created them.
        this.savedFiltersService.reloadForCurrentUser();
    }

    getToken(): string | null {
        const user = this.storageService.getUser();
        return user?.accessToken || null;
    }

    isTokenExpired(): boolean {
        if (!this.tokenExpiresAt) return true;
        return Date.now() >= this.tokenExpiresAt - TokenBuffer.ACCESS_TOKEN;
    }

    isRefreshTokenExpired(): boolean {
        if (!this.refreshTokenExpiresAt) return true;
        return (
            Date.now() >= this.refreshTokenExpiresAt - TokenBuffer.REFRESH_TOKEN
        );
    }

    shouldRefreshToken(): boolean {
        return this.isTokenExpired() && !this.isRefreshTokenExpired();
    }

    private saveAuthData(response: LoginResponse): void {
        this.storageService.setUser({
            ...response,
            refreshToken: undefined,
        });

        if (response.refreshToken) {
            this.cookieService.setCookie(
                'refreshToken',
                response.refreshToken,
                7,
            );
        }

        const now = Date.now();
        this.tokenExpiresAt = now + response.expiresIn * 1000;
        this.refreshTokenExpiresAt = now + response.refreshExpiresIn * 1000;

        this.storageService.setTokenExpiry({
            tokenExpiresAt: this.tokenExpiresAt,
            refreshTokenExpiresAt: this.refreshTokenExpiresAt,
        });

        this.isAuthenticated.set(true);
    }

    private performTokenRefresh(
        refreshToken: string,
    ): Observable<LoginResponse> {
        return this.http
            .post<LoginResponse>(
                `${environment.authApiUrl}/Auth/RefreshToken`,
                {
                    refreshToken: refreshToken,
                },
            )
            .pipe(
                tap((response) => {
                    this.saveAuthData(response);
                }),
                catchError((error) => {
                    // On failure log out and re-throw: every subscriber (the
                    // interceptor's parked requests and the guard) receives
                    // the error so nothing hangs waiting on a token that will
                    // never arrive (AB#9024).
                    this.logout();
                    return throwError(() => error);
                }),
                // Release the shared handle once the refresh settles (success,
                // error, or unsubscribe) so a later refresh is never stuck
                // replaying a stale outcome.
                finalize(() => {
                    this.refreshInFlight$ = null;
                }),
                // One multicast execution shared by all callers; the result —
                // success or error — is replayed to whoever subscribes while
                // it is in flight (AB#8996).
                shareReplay({ bufferSize: 1, refCount: false }),
            );
    }
}
